import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { billingApi } from '../lib/billingApi';
import { useT } from '../lib/i18n';

const STR = {
  fr: {
    no_session:        'Session introuvable.',
    sub_active:        'Ton abonnement est actif.',
    expired:           'La session de paiement a expiré.',
    payment_received:  'Paiement reçu. Synchronisation en cours…',
    finalizing:        'Finalisation en cours…',
    confirming:        'Nous confirmons ton abonnement.',
    welcome:           'Bienvenue dans SoumissionPro !',
    trial_started:     'Ton essai de 3 jours vient de commencer. Profite de toutes les fonctionnalités sans limite.',
    go_dashboard:      'Accéder à mon tableau de bord',
  },
  en: {
    no_session:        'Session not found.',
    sub_active:        'Your subscription is active.',
    expired:           'The payment session has expired.',
    payment_received:  'Payment received. Syncing…',
    finalizing:        'Finalizing…',
    confirming:        'We are confirming your subscription.',
    welcome:           'Welcome to SoumissionPro!',
    trial_started:     'Your 3-day free trial just started. Enjoy every feature without limits.',
    go_dashboard:      'Go to my dashboard',
  },
};

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const navigate = useNavigate();
  const { lang } = useT();
  const T = STR[lang === 'en' ? 'en' : 'fr'];
  const [state, setState] = useState({ loading: true, ok: false, message: '' });

  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, ok: false, message: T.no_session });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const data = await billingApi.checkoutStatus(sessionId);
        if (cancelled) return;
        const sub = data?.subscription_status;
        if (sub && ['active', 'trialing'].includes(sub)) {
          setState({ loading: false, ok: true, message: T.sub_active });
          return;
        }
        if (data?.status === 'expired') {
          setState({ loading: false, ok: false, message: T.expired });
          return;
        }
      } catch (e) {
        // Backend may still be processing the webhook — keep polling silently.
        if (process.env.NODE_ENV === 'development') console.debug('[BillingSuccess] poll attempt failed', e);
      }
      if (attempts < 8) setTimeout(poll, 1500);
      else setState({ loading: false, ok: true, message: T.payment_received });
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, T.no_session, T.sub_active, T.expired, T.payment_received]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#FAF7F2' }}>
      <div className="max-w-md w-full bg-white rounded-2xl p-10 text-center border" style={{ borderColor: '#E5DCC9' }}>
        {state.loading ? (
          <>
            <Loader2 className="w-12 h-12 mx-auto animate-spin mb-4" style={{ color: '#7A1F2B' }} />
            <h1 className="text-2xl font-bold mb-2" style={{ color: '#5A1620' }}>{T.finalizing}</h1>
            <p className="text-sm" style={{ color: '#4A5568' }}>{T.confirming}</p>
          </>
        ) : (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto mb-4" style={{ color: '#16A34A' }} />
            <h1 className="text-2xl font-bold mb-2" style={{ color: '#5A1620' }}>{T.welcome}</h1>
            <p className="text-base mb-6" style={{ color: '#4A5568' }}>
              {state.message || T.trial_started}
            </p>
            <button
              onClick={() => navigate('/')}
              className="w-full py-3 rounded-xl font-semibold text-white"
              style={{ backgroundColor: '#7A1F2B' }}
              data-testid="billing-success-continue-btn"
            >
              {T.go_dashboard}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
